/**
 * @file SupplierCreditAllocationModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-14
 *
 * Allocate supplier credits to bills (Cam, 8 Oct 2026) — the supplier-side
 * AllocateCreditNoteModal. Opened from a credit ("Allocate to bills…") it
 * lists the supplier's (and its supplier group's) bills with money owing;
 * from a bill ("Apply a credit…") their credits with money left. An amount
 * per row, a date (not on or before the Xero lock), the total no more than
 * the credit has left and each amount no more than the bill owes — the
 * server checks it all again (services/supplierCreditService.js) and makes
 * the allocation in Xero first, as the person, then records it here with both
 * documents' figures from Xero.
 *
 * A row that cannot be allocated (another member of the supplier group — Xero
 * allocates only to the credit's own contact — not in Xero yet, dated before
 * the lock) is listed greyed with the reason, never hidden. The allocations
 * already made are listed with Remove (in Xero first).
 *
 * 1.1.0 (2026-10-08, review): each click of Allocate sends a new
 * `request_id` (the Idempotency-Key's nonce — the same amounts allocated
 * twice on purpose are two allocations); what Xero did not make is said, and
 * a refusal on Xero's own figures (read just before) is shown as it comes.
 *
 * 1.2.0 (2026-10-14, Cam — "just check that this workflow works"): the list
 * was the supplier's OLDEST 300 open bills and could not be searched — NWF
 * has 526, so its bills after mid-September never appeared. Now the server
 * lists what this document relates to first (tagged Related), then the open
 * ones NEWEST first, with a search box (number, amount, date, PO, supplier —
 * ~300ms after typing stops, only the newest answer kept) and "Showing the
 * newest N of M". Rows with an amount typed are sent back as include_ids, so
 * a search never drops one. Drafts (a queried bill) are listed greyed.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import DateInput from './DateInput';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import { DecimalInput } from './OrderLinesGrid';
import { useSlideOutButton } from './SlideOutButton';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import {
  allocationTotals, allocationPayload, defaultAllocationDate, round2, newRequestId, BILL_SEARCH_FIELDS, moreToNarrowText
} from '../utils/supplierCredits';
import './SupplierCreditAllocationModal.css';

const labelOf = (r) => r?.supplier_reference || r?.bill_number || (r?.id ? `#${r.id}` : '');

const SEARCH_DEBOUNCE_MS = 300;

function AllocationBody({ data, existing, amounts, setAmount, fill, date, setDate, totals, busy, error, onRemove, search, setSearch, searching }) {
  useSlideOutButton([busy, totals.total, totals.ok]);
  const { doc, candidates, first_allowed_date: firstAllowed } = data;
  const isCredit = doc.kind === 'credit';
  const more = moreToNarrowText(data.matched, Math.min(Number(data.matched) || 0, Number(data.limit) || 0), { what: isCredit ? 'open bills' : 'open credits' });
  const limit = isCredit ? doc.credit_remaining : doc.amount_due;
  return (
    <div className="supplier-credit-allocation-body">
      <div className="sca-summary">
        <div><span className="sca-label">{isCredit ? 'Credit' : 'Bill'}</span><strong>{doc.label}</strong></div>
        <div><span className="sca-label">Supplier</span>{doc.supplier_name || '—'}</div>
        <div><span className="sca-label">Total</span>{formatCurrency(Math.abs(Number(doc.total) || 0))}</div>
        <div>
          <span className="sca-label">{isCredit ? 'Left to allocate' : 'Still owed'}</span>
          <strong>{formatCurrency(limit)}</strong>
        </div>
      </div>

      {doc.block && (
        <p className="sca-hint" role="status">
          <span className="material-symbols-rounded">info</span>{doc.block}
        </p>
      )}

      {existing.length > 0 && (
        <div className="sca-section">
          <h4>Allocated already</h4>
          <div className="table-container">
            <table className="data-table sca-table">
              <thead>
                <tr>
                  <th>{isCredit ? 'Bill' : 'Credit'}</th>
                  <th>Date</th>
                  <th>Made</th>
                  <th className="num">Amount</th>
                  <th className="actions-col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {existing.map(a => {
                  const canRemove = !!a.xero_allocation_id && (!firstAllowed || String(a.allocation_date).slice(0, 10) >= firstAllowed);
                  return (
                    <tr key={a.id}>
                      <td>{isCredit ? (a.bill_reference || a.bill_number) : (a.credit_reference || a.credit_number)}</td>
                      <td>{formatDateShort(a.allocation_date)}</td>
                      <td>{a.source === 'xero' ? 'In Xero' : `Here${a.created_by_name ? ` (${a.created_by_name})` : ''}`}</td>
                      <td className="num">{formatCurrency(a.allocated_amount)}</td>
                      <td className="actions-col">
                        <div className="actions-cell">
                          <button
                            type="button"
                            className={`btn-icon${canRemove && !busy ? '' : ' btn-disabled'}`}
                            data-write-action
                            disabled={!canRemove || busy}
                            title={!a.xero_allocation_id ? 'Not matched to Xero\'s allocation yet — wait for the next bill sync'
                              : !canRemove ? 'Dated on or before the Xero lock — remove it in Xero if it must go'
                                : 'Remove this allocation (in Xero first)'}
                            onClick={() => onRemove(a)}
                          >
                            <span className="material-symbols-rounded">link_off</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sca-section">
        <div className="sca-section-head">
          <h4>{isCredit ? 'Bills with money owing' : 'Credits with money left'}</h4>
          <div className="sca-search">
            <label htmlFor="sca-search">
              Search <SearchHelp fields={isCredit ? BILL_SEARCH_FIELDS : [...BILL_SEARCH_FIELDS, 'PO no. the credit relates to']} amounts />
            </label>
            <input
              id="sca-search"
              type="text"
              className="styled-input"
              autoComplete="off"
              placeholder={isCredit ? 'Number, amount, date, PO…' : 'Credit no., amount, date, PO…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="sca-date">
            <label htmlFor="sca-date">Allocation date</label>
            <DateInput id="sca-date" value={date} min={firstAllowed || undefined} onChange={(e) => setDate(e.target.value)} disabled={busy} />
          </div>
        </div>
        {(more || searching) && <p className="sca-more" role="status">{searching ? 'Searching…' : more}</p>}
        {candidates.length === 0 ? (
          <p className="sca-empty">
            {search.trim()
              ? `Nothing ${isCredit ? 'owing' : 'left'} matches “${search.trim()}”.`
              : (isCredit ? 'No bill of this supplier has money owing.' : 'No credit of this supplier has money left.')}
          </p>
        ) : (
          <div className="table-container">
            <table className="data-table sca-table">
              <thead>
                <tr>
                  <th>{isCredit ? 'Bill' : 'Credit'}</th>
                  <th>Supplier</th>
                  <th>Date</th>
                  <th className="num">Total</th>
                  <th className="num">{isCredit ? 'Owed' : 'Left'}</th>
                  <th className="num sca-amount-col">Allocate</th>
                  <th className="actions-col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {candidates.map(r => {
                  const cap = isCredit ? r.amount_due : r.credit_remaining;
                  const over = totals.rowsOver.includes(Number(r.id));
                  return (
                    <tr key={r.id} className={r.block ? 'sca-blocked' : undefined} title={r.block || undefined}>
                      <td>
                        {labelOf(r)}
                        {r.related && (
                          <span className="sca-related" title="This credit relates to this bill">Related</span>
                        )}
                        {r.block && <div className="sca-row-reason">{r.block}</div>}
                      </td>
                      <td>{r.supplier_name || '—'}</td>
                      <td>{formatDateShort(r.bill_date)}</td>
                      <td className="num">{formatCurrency(Math.abs(Number(r.total) || 0))}</td>
                      <td className="num">{formatCurrency(cap)}</td>
                      <td className={`num sca-amount-col${over ? ' sca-over' : ''}`}>
                        <DecimalInput
                          value={amounts[r.id] ?? ''}
                          onValueChange={(v) => setAmount(r.id, v)}
                          disabled={!!r.block || busy}
                          placeholder="0.00"
                          aria-label={`Amount for ${labelOf(r)}`}
                          step="0.01"
                          min="0"
                        />
                      </td>
                      <td className="actions-col">
                        <div className="actions-cell">
                          <button
                            type="button"
                            className={`btn-icon${r.block || busy ? ' btn-disabled' : ''}`}
                            disabled={!!r.block || busy}
                            title={r.block || `Allocate as much as fits (${isCredit ? 'what the bill owes' : 'what the credit has left'}, up to what is left)`}
                            onClick={() => fill(r)}
                          >
                            <span className="material-symbols-rounded">input</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`sca-totals${totals.over ? ' sca-over' : ''}`}>
        Allocating <strong>{formatCurrency(totals.total)}</strong> of {formatCurrency(limit)} {isCredit ? 'left on the credit' : 'owed on the bill'}
        {totals.over && ' — more than there is'}
        {totals.rowsOver.length > 0 && ` — ${totals.rowsOver.length} amount${totals.rowsOver.length === 1 ? ' is' : 's are'} more than ${isCredit ? 'the bill owes' : 'the credit has left'}`}
      </p>
      {error && (
        <div className="sca-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {number} props.docId — the credit or the bill it was opened from
 * @param {() => void} props.onClose — the caller refreshes what is behind
 * @param {(message: string) => void} [props.onDone]
 */
function SupplierCreditAllocationModal({ docId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [existing, setExisting] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const latest = useLatestRequest();
  // Read when a request is made: the search box, and the rows with an amount
  // typed (sent back as include_ids so a search never drops one).
  const searchRef = useRef('');
  searchRef.current = search;
  const amountsRef = useRef({});
  amountsRef.current = amounts;

  const candidatesUrl = () => {
    const qs = new URLSearchParams();
    const q = searchRef.current.trim();
    if (q) qs.set('search', q);
    const typed = Object.entries(amountsRef.current).filter(([, v]) => (parseFloat(v) || 0) > 0).map(([id]) => id);
    if (typed.length) qs.set('include_ids', typed.join(','));
    const s = qs.toString();
    return `${API_URL}/api/bills/${docId}/allocation-candidates${s ? `?${s}` : ''}`;
  };

  const loadCandidates = async () => {
    const ticket = latest.begin();
    setSearching(true);
    try {
      const c = await fetch(candidatesUrl(), { credentials: 'include' }).then(r => r.json());
      if (!latest.isLatest(ticket)) return;
      if (!c.success) throw new Error(c.error || 'The bills could not be read');
      setData(c);
      setDate(prev => prev || defaultAllocationDate(c.first_allowed_date));
    } catch (err) {
      if (latest.isLatest(ticket)) setError(err.message);
    } finally {
      if (latest.isLatest(ticket)) setSearching(false);
    }
  };

  const load = async () => {
    const [, a] = await Promise.all([
      loadCandidates(),
      fetch(`${API_URL}/api/bills/${docId}/credit-allocations`, { credentials: 'include' }).then(r => r.json()).catch(() => ({}))
    ]);
    setExisting(a && a.success ? (a.allocations || []) : []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // A search: ~300ms after typing stops (not on the first render — load() has it).
  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return undefined; }
    const timer = setTimeout(() => { loadCandidates(); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const kind = data?.doc?.kind || 'credit';
  const limit = kind === 'credit' ? data?.doc?.credit_remaining : data?.doc?.amount_due;
  const totals = useMemo(() => allocationTotals({ kind, limit, rows: data?.candidates || [], amounts }),
    [kind, limit, data, amounts]);

  const setAmount = (id, v) => setAmounts(prev => ({ ...prev, [id]: v }));
  const fill = (r) => {
    const cap = kind === 'credit' ? Number(r.amount_due) : Number(r.credit_remaining);
    const others = round2(totals.total - (Number(amounts[r.id]) || 0));
    const room = round2(Math.max(0, (Number(limit) || 0) - others));
    setAmount(r.id, String(round2(Math.min(cap, room))));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${docId}/allocate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        // A new id per click: a resend of this request is the same request
        // to Xero; another click is another allocation.
        body: JSON.stringify({ ...allocationPayload(kind, totals.chosen, date), request_id: newRequestId() })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) {
        const msg = d.error || (Array.isArray(d.failed) && d.failed.map(f => f.error).join(' ')) || 'The allocation was not made.';
        // Some went (from a bill: one credit per Xero call): say which, and reload.
        if (d.done && d.done.length) {
          await load();
          setAmounts({});
        }
        setError(msg);
        return;
      }
      onDone && onDone(`Allocated ${formatCurrency(totals.total)} in Xero and here.`);
      onClose();
    } catch (err) {
      setError(`The allocation was not made: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    setRemoving(null);
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/credit-allocations/${a.id}`, { method: 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) { setError(d.error || 'The allocation was not removed.'); return; }
      await load();
      onDone && onDone(`Allocation of ${formatCurrency(a.allocated_amount)} removed, in Xero and here.`);
    } catch (err) {
      setError(`The allocation was not removed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const blocked = !data || !!data.doc?.block || data.ready === false;
  return (
    <>
      <Modal
        isOpen
        onClose={busy ? () => {} : onClose}
        title={kind === 'credit' ? `Allocate ${data?.doc?.label || 'credit'} to bills` : `Apply a credit to ${data?.doc?.label || 'the bill'}`}
        size="xl"
        className="supplier-credit-allocation"
        footer={
          <div className="buttons slideout">
            <div>
              <button type="button" className="btn-slideout" data-read-safe onClick={onClose} disabled={busy}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Close</span>
              </button>
            </div>
            <div>
              <button
                type="button"
                className="btn-slideout btn-slideout-primary"
                data-write-action
                onClick={submit}
                disabled={busy || blocked || !totals.ok || !date}
                title={data?.ready === false ? 'Needs the supplier credits migration, which has not been run yet'
                  : data?.doc?.block || (!totals.ok ? 'Enter amounts within the limits first' : 'Allocate in Xero, then here')}
              >
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'credit_score'}</span>
                </div>
                <span className="btn-label">{busy ? 'Allocating…' : 'Allocate'}</span>
              </button>
            </div>
          </div>
        }
      >
        {!data ? (error ? <div className="sca-error" role="alert"><span>{error}</span></div> : <LoadingSpinner />) : (
          <AllocationBody
            data={data} existing={existing} amounts={amounts} setAmount={setAmount} fill={fill}
            date={date} setDate={setDate} totals={totals} busy={busy} error={error} onRemove={setRemoving}
            search={search} setSearch={setSearch} searching={searching}
          />
        )}
      </Modal>
      <ConfirmDialog
        isOpen={!!removing}
        title="Remove this allocation?"
        message={removing ? `The ${formatCurrency(removing.allocated_amount)} allocation is removed in Xero first, then here. The bill owes it again and the credit has it back.` : ''}
        confirmText="Remove"
        cancelText="Keep it"
        type="warning"
        onConfirm={() => remove(removing)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

export default SupplierCreditAllocationModal;
